/**
 * Attendance Report Page
 * Refactored to use modular components
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { format, startOfMonth, endOfMonth, startOfYear, endOfYear, parseISO, eachDayOfInterval, getDay } from "date-fns";
import { ArrowLeft, Printer, FileSpreadsheet, Download, Trash2, FileText, Grid, Table, Users, CalendarDays } from "lucide-react";
import { exportMonthlyAttendancePDF, exportAttendanceToExcel, exportToExcel } from "@/utils/pdfExport";
import AppShell from "@/components/layout/AppShell";
import PageHero, { HERO_IMAGES } from "@/components/common/PageHero";
import FeatureGate from "@/components/FeatureGate";

// Import modular components
import { ReportControls, OperatingDaysConfig, ReportSummary, ChildrenRosterTab } from "@/components/attendance-report";
import PrintReportHeader from "@/components/common/PrintReportHeader";
import { formatBirthdate } from "@/utils/formatBirthdate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category filter options + labels — mirrors the rest of the app (Children Roster, etc.)
const CATEGORY_OPTIONS = [
  { value: "all", label: "All Children" },
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "school_age", label: "School Age" },
  { value: "after_school", label: "After School" },
  { value: "uncategorized", label: "Uncategorized" },
];
const normCat = (c) => {
  const v = (c || "").toString().toLowerCase();
  if (v === "regular") return "full_time";
  if (!v || v === "uncategorized") return "uncategorized";
  return v;
};

const AttendanceReportPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { organization } = useAuth();
  const centerName = organization?.name || '';
  const [reportType, setReportType] = useState("month");
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), "yyyy-MM"));
  const [selectedYear, setSelectedYear] = useState(format(new Date(), "yyyy"));
  const [startDate, setStartDate] = useState(format(startOfMonth(new Date()), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(endOfMonth(new Date()), "yyyy-MM-dd"));
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [diagnostics, setDiagnostics] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [monthlyStats, setMonthlyStats] = useState(null);
  
  const [operatingDays, setOperatingDays] = useState({
    0: false, 1: true, 2: true, 3: true, 4: true, 5: true, 6: false
  });
  const [showOperatingDaysConfig, setShowOperatingDaysConfig] = useState(false);
  const [editableAttendance, setEditableAttendance] = useState([]);
  // Category filter for the attendance table + all exports. "all" (default) = everyone.
  const [categoryFilter, setCategoryFilter] = useState("all");
  const matchesCategory = (cat) => categoryFilter === "all" || normCat(cat) === categoryFilter;
  const computeSummary = (list, totalOperatingDays) => {
    const totalPresentDays = list.reduce((s, c) => s + (c.daysPresent || 0), 0);
    const totalHours = list.reduce((s, c) => s + ((c.avgHoursPerDay || 0) * (c.daysPresent || 0)), 0);
    return {
      totalOperatingDays,
      totalChildren: list.length,
      avgDailyAttendance: totalOperatingDays > 0 ? totalPresentDays / totalOperatingDays : 0,
      avgHoursPerChild: list.length > 0 ? totalHours / list.length : 0,
    };
  };

  const months = [
    { value: "01", label: language === 'es' ? "Enero" : "January" },
    { value: "02", label: language === 'es' ? "Febrero" : "February" },
    { value: "03", label: language === 'es' ? "Marzo" : "March" },
    { value: "04", label: language === 'es' ? "Abril" : "April" },
    { value: "05", label: language === 'es' ? "Mayo" : "May" },
    { value: "06", label: language === 'es' ? "Junio" : "June" },
    { value: "07", label: language === 'es' ? "Julio" : "July" },
    { value: "08", label: language === 'es' ? "Agosto" : "August" },
    { value: "09", label: language === 'es' ? "Septiembre" : "September" },
    { value: "10", label: language === 'es' ? "Octubre" : "October" },
    { value: "11", label: language === 'es' ? "Noviembre" : "November" },
    { value: "12", label: language === 'es' ? "Diciembre" : "December" },
  ];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  // ============== HELPERS ==============
  
  const getDateRange = () => {
    let start, end;
    if (reportType === "month") {
      const [year, month] = selectedMonth.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      start = format(startOfMonth(date), "yyyy-MM-dd");
      end = format(endOfMonth(date), "yyyy-MM-dd");
    } else if (reportType === "year") {
      const date = new Date(parseInt(selectedYear), 0, 1);
      start = format(startOfYear(date), "yyyy-MM-dd");
      end = format(endOfYear(date), "yyyy-MM-dd");
    } else {
      start = startDate;
      end = endDate;
    }
    return { start, end };
  };

  const calculateOperatingDays = (start, end) => {
    const startDateObj = parseISO(start);
    const endDateObj = parseISO(end);
    const allDays = eachDayOfInterval({ start: startDateObj, end: endDateObj });
    return allDays.filter(day => operatingDays[getDay(day)]).length;
  };

  const parseTimeToMinutes = (timeStr) => {
    if (!timeStr) return null;
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
    if (!match) return null;
    let hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    const period = match[3];
    if (period) {
      if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
      if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
    }
    return hours * 60 + minutes;
  };

  // ============== REPORT GENERATION ==============
  
  const generateReport = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      
      // Fetch both signins report and children data
      const [reportResponse, childrenResponse] = await Promise.all([
        axios.get(`${API}/signins/report?start_date=${start}&end_date=${end}`),
        axios.get(`${API}/children`)
      ]);
      
      const data = {
        records: reportResponse.data.records || [],
        summary: reportResponse.data.summary || {},
        startDate: start,
        endDate: end
      };
      setReportData(data);
      
      // Create a map of children by ID for birthdate lookup
      const childrenById = new Map();
      (childrenResponse.data || []).forEach(child => {
        childrenById.set(child.id, child);
      });
      
      const stats = calculateMonthlyStats(data.records, start, end, childrenById);
      setMonthlyStats(stats);
      setEditableAttendance(stats.childAttendance);
      toast.success(language === 'es' ? "Informe generado" : "Report generated");
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error(language === 'es' ? "Error al generar informe" : "Failed to generate report");
    }
    setLoading(false);
  };

  const calculateMonthlyStats = (records, startDate, endDate, childrenById = new Map()) => {
    if (!records || records.length === 0) {
      return {
        summary: { totalOperatingDays: calculateOperatingDays(startDate, endDate), totalChildren: 0, avgDailyAttendance: 0, avgHoursPerChild: 0 },
        childAttendance: [],
        dateRange: { start: startDate, end: endDate }
      };
    }

    const totalOperatingDays = calculateOperatingDays(startDate, endDate);
    const childrenMap = new Map();

    records.forEach(record => {
      const childId = record.childId || record.childName;
      if (!childrenMap.has(childId)) {
        // Get birthdate from children data
        const childData = childrenById.get(childId);
        childrenMap.set(childId, {
          id: childId,
          name: record.childName,
          category: record.category || 'Uncategorized',
          birthdate: childData?.birthdate || null,
          presentDates: new Set(),
          totalMinutes: 0
        });
      }
      const child = childrenMap.get(childId);
      const isPresent = record.dropOffTime || record.attendance === 'present' ||
                       record.amParentDropOffTime || record.pmSchoolAgeDropOffTime;
      if (isPresent) {
        child.presentDates.add(record.date);
        
        // Calculate hours based on category
        // Full Time / Part Time: dropOffTime → pickUpTime
        // School Age: amParentDropOffTime → pmParentPickupTime (or pmSchoolAgeDropOffTime if no parent pickup)
        let startTime = null;
        let endTime = null;
        
        if (record.category === 'school_age') {
          // School Age: Use AM bus pickup as start, parent pickup (or bus dropoff) as end
          if (record.amParentDropOffTime) {
            startTime = parseTimeToMinutes(record.amParentDropOffTime);
          }
          if (record.pmParentPickupTime) {
            endTime = parseTimeToMinutes(record.pmParentPickupTime);
          } else if (record.pmSchoolAgeDropOffTime) {
            endTime = parseTimeToMinutes(record.pmSchoolAgeDropOffTime);
          }
        } else {
          // Full Time / Part Time / Others: Use dropOff → pickUp
          if (record.dropOffTime) {
            startTime = parseTimeToMinutes(record.dropOffTime);
          }
          if (record.pickUpTime) {
            endTime = parseTimeToMinutes(record.pickUpTime);
          }
        }
        
        if (startTime !== null && endTime !== null && endTime > startTime) {
          child.totalMinutes += (endTime - startTime);
        }
      }
    });

    // Calculate age helper
    const calculateAge = (birthdate) => {
      if (!birthdate) return null;
      try {
        const birthDate = parseISO(birthdate);
        const today = new Date();
        let years = today.getFullYear() - birthDate.getFullYear();
        let months = today.getMonth() - birthDate.getMonth();
        
        if (months < 0) {
          years--;
          months += 12;
        }
        if (today.getDate() < birthDate.getDate()) {
          months--;
          if (months < 0) {
            years--;
            months += 12;
          }
        }
        
        if (years > 0) {
          return `${years}y ${months}m`;
        }
        return `${months}m`;
      } catch {
        return null;
      }
    };

    const childAttendance = Array.from(childrenMap.values()).map(child => {
      const daysPresent = child.presentDates.size;
      const daysAbsent = Math.max(0, totalOperatingDays - daysPresent);
      const avgHoursPerDay = daysPresent > 0 ? (child.totalMinutes / 60) / daysPresent : 0;
      return {
        id: child.id,
        name: child.name,
        category: child.category,
        birthdate: child.birthdate,
        age: calculateAge(child.birthdate),
        daysPresent,
        daysAbsent,
        avgHoursPerDay
      };
    }).sort((a, b) => a.name.localeCompare(b.name));

    const totalPresentDays = childAttendance.reduce((sum, c) => sum + c.daysPresent, 0);
    const avgDailyAttendance = totalOperatingDays > 0 ? totalPresentDays / totalOperatingDays : 0;
    const totalHours = childAttendance.reduce((sum, c) => sum + (c.avgHoursPerDay * c.daysPresent), 0);
    const avgHoursPerChild = childAttendance.length > 0 ? totalHours / childAttendance.length : 0;

    return {
      summary: { totalOperatingDays, totalChildren: childAttendance.length, avgDailyAttendance, avgHoursPerChild },
      childAttendance,
      dateRange: { start: startDate, end: endDate }
    };
  };

  // ============== EDITING HANDLERS ==============
  
  const updateDaysPresent = (childId, newValue) => {
    const value = parseInt(newValue) || 0;
    setEditableAttendance(prev => prev.map(child => {
      if (child.id === childId) {
        const totalOpDays = monthlyStats?.summary?.totalOperatingDays || 0;
        return { ...child, daysPresent: value, daysAbsent: Math.max(0, totalOpDays - value) };
      }
      return child;
    }));
  };

  const updateDaysAbsent = (childId, newValue) => {
    const value = parseInt(newValue) || 0;
    setEditableAttendance(prev => prev.map(child => {
      if (child.id === childId) {
        const totalOpDays = monthlyStats?.summary?.totalOperatingDays || 0;
        return { ...child, daysAbsent: value, daysPresent: Math.max(0, totalOpDays - value) };
      }
      return child;
    }));
  };

  const handleOperatingDayToggle = (day) => {
    setOperatingDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  // ============== EXPORT HANDLERS ==============
  
  const handlePrint = () => window.print();

  const handleExportPDF = async () => {
    if (!monthlyStats || !editableAttendance.length) {
      toast.error("Please generate a report first");
      return;
    }
    try {
      const filteredChildren = editableAttendance.filter(c => matchesCategory(c.category));
      if (!filteredChildren.length) {
        toast.error("No children in the selected category");
        return;
      }
      const summary = categoryFilter === "all"
        ? monthlyStats.summary
        : computeSummary(filteredChildren, monthlyStats.summary.totalOperatingDays);
      const exportData = { summary, childAttendance: filteredChildren, dateRange: monthlyStats.dateRange };
      await exportMonthlyAttendancePDF(exportData, monthlyStats.dateRange, centerName);
      toast.success(language === 'es' ? "PDF descargado" : "PDF downloaded");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    }
  };

  const handleExportExcel = () => {
    if (!reportData?.records) {
      toast.error("Please generate a report first");
      return;
    }
    try {
      const records = reportData.records.filter(r => matchesCategory(r.category));
      exportAttendanceToExcel(records, { start: reportData.startDate, end: reportData.endDate });
      toast.success(language === 'es' ? "Excel descargado" : "Excel downloaded");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel");
    }
  };

  const handleExportSummaryExcel = () => {
    if (!editableAttendance.length) {
      toast.error("Please generate a report first");
      return;
    }
    try {
      const filteredChildren = editableAttendance.filter(c => matchesCategory(c.category));
      exportToExcel(
        filteredChildren.map(c => ({
          'Child Name': c.name, 'Category': c.category, 'Days Present': c.daysPresent,
          'Days Absent': c.daysAbsent, 'Avg Hours/Day': c.avgHoursPerDay.toFixed(1)
        })),
        `attendance_summary_${monthlyStats.dateRange.start}_to_${monthlyStats.dateRange.end}`,
        'Summary'
      );
      toast.success(language === 'es' ? "Resumen Excel descargado" : "Summary Excel downloaded");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel");
    }
  };

  const exportToCSV = () => {
    if (!reportData?.records) return;
    const headers = ["Date", "Child Name", "Birthdate", "Age", "Category", "Drop-Off Person", "Drop-Off Time", "Pick-Up Person", "Pick-Up Time", "Notes"];
    
    // Create a map of children from editableAttendance for birthdate/age lookup
    const childDataMap = new Map();
    (editableAttendance || []).forEach(child => {
      childDataMap.set(child.id, { birthdate: child.birthdate, age: child.age });
    });
    
    const rows = reportData.records.filter(record => matchesCategory(record.category)).map(record => {
      const childData = childDataMap.get(record.childId) || {};
      const birthdateFormatted = formatBirthdate(childData.birthdate);
      return [
        record.date, record.childName, birthdateFormatted, childData.age || '',
        record.category, record.dropOffPerson || "", record.dropOffTime || "",
        record.pickUpPerson || "", record.pickUpTime || "", record.notes || ""
      ];
    });
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `attendance_report_${reportData.startDate}_to_${reportData.endDate}.csv`;
    link.click();
    toast.success(language === 'es' ? "CSV descargado" : "CSV downloaded");
  };

  const runDiagnostics = async () => {
    const { start, end } = getDateRange();
    try {
      const response = await axios.get(`${API}/signins/diagnostics?start_date=${start}&end_date=${end}`);
      setDiagnostics(response.data);
    } catch (error) {
      console.error("Error running diagnostics:", error);
      toast.error("Failed to run diagnostics");
    }
  };

  const cleanupOrphaned = async () => {
    if (!diagnostics || diagnostics.orphaned_signins === 0) {
      toast.info("No orphaned records to clean up");
      return;
    }
    if (!window.confirm(`Delete ${diagnostics.orphaned_signins} orphaned records?`)) return;
    setCleaningUp(true);
    const { start, end } = getDateRange();
    try {
      const response = await axios.post(`${API}/signins/cleanup-orphaned?start_date=${start}&end_date=${end}`);
      toast.success(response.data.message);
      setDiagnostics(null);
      if (reportData) generateReport();
    } catch (error) {
      console.error("Error cleaning up:", error);
      toast.error("Failed to clean up orphaned records");
    }
    setCleaningUp(false);
  };

  const handleBulkDelete = async () => {
    const { start, end } = getDateRange();
    const confirmMsg = `Delete ALL sign-in records from ${start} to ${end}?\n\nThis action cannot be undone!`;
    if (!window.confirm(confirmMsg)) return;
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/signins/bulk-delete?start_date=${start}&end_date=${end}`);
      toast.success(response.data.message);
      setReportData(null);
      setMonthlyStats(null);
      setEditableAttendance([]);
    } catch (error) {
      console.error("Error deleting records:", error);
      toast.error("Failed to delete records");
    }
    setDeleting(false);
  };

  // ============== RENDER ==============

  // Category-filtered list for the on-screen table (default "all" = everyone). Preserves name order.
  const displayedAttendance = editableAttendance.filter((c) => matchesCategory(c.category));
  const displayedSummary = monthlyStats
    ? (categoryFilter === "all"
        ? monthlyStats.summary
        : computeSummary(displayedAttendance, monthlyStats.summary.totalOperatingDays))
    : null;
  
  return (
    <AppShell pageTitle={language === 'es' ? 'Informes' : 'Reports'} breadcrumb>
      {/* Hero Banner */}
      <PageHero 
        imageUrl={HERO_IMAGES.attendanceReport}
        title="Attendance Report"
        subtitle="Generate and export attendance reports"
        altText="Multicultural families at daycare sign-in"
      />
      
      <FeatureGate feature="attendance_reports">
      <div data-testid="attendance-report-page" className="relative z-10 print:p-0">
        {/* Print Header - Only visible when printing */}
        <PrintReportHeader reportTitle="Attendance Report" />
        
        {/* Tabs for different report types */}
        <Tabs defaultValue="attendance" className="w-full">
          <TabsList className="grid grid-cols-2 gap-2 bg-white p-2 rounded-xl mb-6 shadow-card border border-line print:hidden" data-testid="report-tabs">
            <TabsTrigger 
              value="attendance" 
              className="rounded-lg data-[state=active]:bg-brand-primary data-[state=active]:text-white flex items-center gap-2"
            >
              <CalendarDays className="w-4 h-4" />
              {language === 'es' ? 'Informe de Asistencia' : 'Attendance Report'}
            </TabsTrigger>
            <TabsTrigger 
              value="roster" 
              className="rounded-lg data-[state=active]:bg-brand-secondary data-[state=active]:text-white flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              {language === 'es' ? 'Lista de Niños' : 'Children Roster'}
            </TabsTrigger>
          </TabsList>

          {/* Attendance Report Tab */}
          <TabsContent value="attendance">
            {/* Report Controls */}
            <div className="space-y-4 print:hidden">
              <ReportControls
                reportType={reportType}
                setReportType={setReportType}
                selectedMonth={selectedMonth}
                setSelectedMonth={setSelectedMonth}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
                months={months}
                years={years}
                onGenerate={generateReport}
                loading={loading}
                showOperatingDaysConfig={showOperatingDaysConfig}
                setShowOperatingDaysConfig={setShowOperatingDaysConfig}
                language={language}
              />

              {showOperatingDaysConfig && (
                <OperatingDaysConfig operatingDays={operatingDays} onToggle={handleOperatingDayToggle} />
              )}
            </div>

            {/* Report Summary */}
            {monthlyStats && (
              <div className="mt-6 space-y-6">
                <ReportSummary summary={displayedSummary} dateRange={`${monthlyStats.dateRange.start} to ${monthlyStats.dateRange.end}`} />

                {/* Export Buttons */}
                <div className="flex flex-wrap gap-2 print:hidden">
                  <Button onClick={handlePrint} variant="outline" className="border-blue-300 text-blue-700">
                    <Printer className="w-4 h-4 mr-2" />Print
                  </Button>
                  <Button onClick={handleExportPDF} variant="outline" className="border-red-300 text-red-700">
                    <FileText className="w-4 h-4 mr-2" />Export PDF
                  </Button>
                  <Button onClick={handleExportSummaryExcel} variant="outline" className="border-green-300 text-green-700">
                    <Table className="w-4 h-4 mr-2" />Summary Excel
                  </Button>
                  <Button onClick={handleExportExcel} variant="outline" className="border-blue-300 text-blue-700">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />Full Excel
                  </Button>
                  <Button onClick={exportToCSV} variant="outline" className="border-purple-300 text-purple-700">
                    <Download className="w-4 h-4 mr-2" />CSV
                  </Button>
                  <Button onClick={runDiagnostics} variant="outline" className="border-gray-300 text-gray-700">
                    Run Diagnostics
                  </Button>
                  <Button onClick={handleBulkDelete} variant="outline" className="border-red-500 text-red-700" disabled={deleting}>
                    <Trash2 className="w-4 h-4 mr-2" />{deleting ? "Deleting..." : "Delete Range"}
                  </Button>
                </div>

                {/* Diagnostics */}
                {diagnostics && (
                  <Card className="bg-yellow-50 border-yellow-200">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-yellow-800">Diagnostics Results</p>
                          <p className="text-sm text-yellow-700">
                            Total records: {diagnostics.total_signins} | Orphaned: {diagnostics.orphaned_signins} | Valid: {diagnostics.valid_signins}
                          </p>
                        </div>
                        {diagnostics.orphaned_signins > 0 && (
                          <Button onClick={cleanupOrphaned} disabled={cleaningUp} className="bg-yellow-500 hover:bg-yellow-600 text-white">
                            {cleaningUp ? "Cleaning..." : `Clean up ${diagnostics.orphaned_signins} orphaned`}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Attendance Table */}
                <Card className="bg-white/80 backdrop-blur-sm border-2 border-blue-200">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <CardTitle className="text-xl text-blue-800">
                        {language === 'es' ? 'Asistencia por Niño' : 'Attendance by Child'}
                      </CardTitle>
                      <div className="flex items-center gap-2 print:hidden" data-testid="attendance-category-filter">
                        <span className="text-sm text-gray-600">
                          {language === 'es' ? 'Categoría' : 'Category'}
                        </span>
                        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                          <SelectTrigger className="w-40 border-blue-200" data-testid="attendance-category-filter-trigger">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CATEGORY_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value} data-testid={`category-option-${opt.value}`}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-blue-100">
                            <th className="border border-blue-200 p-2 text-left text-blue-800">Name</th>
                            <th className="border border-blue-200 p-2 text-center text-blue-800">DOB</th>
                            <th className="border border-blue-200 p-2 text-center text-blue-800">Age</th>
                            <th className="border border-blue-200 p-2 text-left text-blue-800">Category</th>
                            <th className="border border-blue-200 p-2 text-center text-blue-800">Days Present</th>
                            <th className="border border-blue-200 p-2 text-center text-blue-800">Days Absent</th>
                            <th className="border border-blue-200 p-2 text-center text-blue-800">Avg Hours/Day</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayedAttendance.map((child) => (
                            <tr key={child.id} className="hover:bg-blue-50">
                              <td className="border border-blue-200 p-2 font-medium">{child.name}</td>
                              <td className="border border-blue-200 p-2 text-center text-gray-600 text-sm">
                                {formatBirthdate(child.birthdate)}
                              </td>
                              <td className="border border-blue-200 p-2 text-center text-blue-600 font-medium text-sm">
                                {child.age || '-'}
                              </td>
                              <td className="border border-blue-200 p-2 text-blue-600">{child.category}</td>
                              <td className="border border-blue-200 p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  value={child.daysPresent}
                                  onChange={(e) => updateDaysPresent(child.id, e.target.value)}
                                  className="w-20 text-center border-blue-200 mx-auto"
                                />
                              </td>
                              <td className="border border-blue-200 p-2 text-center">
                                <Input
                                  type="number"
                                  min="0"
                                  value={child.daysAbsent}
                                  onChange={(e) => updateDaysAbsent(child.id, e.target.value)}
                                  className="w-20 text-center border-blue-200 mx-auto"
                                />
                              </td>
                              <td className="border border-blue-200 p-2 text-center text-blue-600 font-medium">
                                {child.avgHoursPerDay.toFixed(1)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* No Data Message */}
            {!monthlyStats && !loading && (
              <Card className="mt-6 bg-blue-50 border-2 border-blue-200">
                <CardContent className="p-12 text-center">
                  <FileSpreadsheet className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                  <p className="text-xl text-blue-700">
                    {language === 'es' ? 'Selecciona un período y genera el informe' : 'Select a period and generate the report'}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Children Roster Tab */}
          <TabsContent value="roster">
            <ChildrenRosterTab />
          </TabsContent>
        </Tabs>
      </div>
      </FeatureGate>
    </AppShell>
  );
};

export default AttendanceReportPage;
